import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CollectionCard } from './CollectionCard';
import { CreateCollectionModal } from './CreateCollectionModal';
import { collectionsApi } from '../services/collectionsApi';
import { CollectionListItem, GenerateCollectionDTO } from '../types/collection';
import { useAuth } from '../contexts/FirebaseAuthContext';
import { useUsage } from '../contexts/UsageContext';
import { usePinManagement } from '../hooks/usePinManagement';
import { useAuthModal } from '../hooks/useAuthModal';
import { useCollectionStream } from '../hooks/useCollectionStream';
import { AuthRequiredModal } from './AuthRequiredModal';
import { LoginModal } from './LoginModal';
import LoadingModal from './LoadingModal';
import { mockSystemCollections } from '../data/mockSystemCollections';
import { savePendingAction } from '../utils/authRedirectState';
import '../styles/MyCollectionsTab.css';

interface MyCollectionsTabProps {
  onTabChange?: (tab: 'my-collections' | 'browse' | 'problems') => void;
}

export const MyCollectionsTab: React.FC<MyCollectionsTabProps> = ({ onTabChange }) => {
  const navigate = useNavigate();
  const { userProfile, isAuthenticated, isLoading: authLoading } = useAuth();
  const { openPaywall } = useUsage();
  const {
    pinCollection,
    unpinCollection,
    isCollectionPinned,
    sortCollections,
    maxPinnedCollections,
    pinData
  } = usePinManagement();
  const {
    showAuthRequiredModal,
    showLoginModal,
    requireAuth,
    handleOk,
    handleLoginSuccess,
    handleClose
  } = useAuthModal();
  const { state: streamState, startStream, cancelStream } = useCollectionStream();
  const [collections, setCollections] = useState<CollectionListItem[]>([]);
  const [systemCollections, setSystemCollections] = useState<CollectionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [isGeneratingProblems, setIsGeneratingProblems] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [draggedItem, setDraggedItem] = useState<CollectionListItem | null>(null);
  const [draggedOverId, setDraggedOverId] = useState<string | null>(null);

  useEffect(() => {
    // Only load collections when auth is ready and user is authenticated
    if (!authLoading) {
      if (isAuthenticated && userProfile) {
        loadCollections();
      } else {
        // Clear user collections if not authenticated
        setCollections([]);
        setLoading(false);
      }
      // Always load system collections for "Browse More" section
      loadSystemCollections();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, userProfile]);

  // Cleanup: cancel any in-progress streaming when component unmounts
  useEffect(() => {
    return () => {
      cancelStream();
    };
  }, [cancelStream]);

  // Listen for auth redirect completion to resume pending actions
  useEffect(() => {
    const handleAuthRedirect = (e: Event) => {
      const customEvent = e as CustomEvent;
      const { pendingAction } = customEvent.detail;

      if (pendingAction?.type === 'create_collection') {
        // Resume create collection modal
        openCreateModal();
      } else if (pendingAction?.type === 'generate_collection') {
        // Resume AI generation with saved parameters
        const generateDTO = pendingAction.data as GenerateCollectionDTO;
        performGenerateByAI(generateDTO);
      }
    };

    window.addEventListener('auth-redirect-complete', handleAuthRedirect);
    return () => window.removeEventListener('auth-redirect-complete', handleAuthRedirect);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSystemCollections = async () => {
    try {
      const response = await collectionsApi.getSystemCollections();
      // Response is already an array from the API service
      const collectionsArray = Array.isArray(response) ? response : 
                              (response.data || response.collections || []);
      
      // Transform to CollectionListItem format with default stats
      const transformedCollections = collectionsArray.map((col: any) => ({
        collection: {
          ...col,
          // System collections have totalProblems at root level, need to create stats object
          stats: col.stats || { 
            totalProblems: col.totalProblems || 0, 
            difficulty: col.difficulty ? {
              easy: col.difficulty.easy || 0,
              medium: col.difficulty.medium || 0,
              hard: col.difficulty.hard || 0
            } : { easy: 0, medium: 0, hard: 0 }
          }
        },
        completedCount: 0,
        completionPercentage: 0,
        lastPracticed: undefined
      }));
      setSystemCollections(transformedCollections);
    } catch (error) {
      console.error('Failed to load system collections, using fallback data:', error);
      // Use mock data as fallback when API fails
      const fallbackCollections = mockSystemCollections.map(col => ({
        collection: col,
        completedCount: 0,
        completionPercentage: 0,
        lastPracticed: undefined
      }));
      setSystemCollections(fallbackCollections);
    }
  };

  const loadCollections = async () => {
    try {
      setLoading(true);
      // Pass user ID from auth context to ensure we have the right user
      console.log('Loading collections for user:', userProfile?.uid);
      const response = await collectionsApi.getCollections(userProfile?.uid);
      console.log('Collections API response:', response);
      
      // Transform collections to expected format
      // Response is already an array from the API service
      const collectionsData = Array.isArray(response) ? response : (response.data || response.collections || []);
      const transformedCollections = collectionsData.map((col: any) => {
        // Handle both direct Collection objects and CollectionListItem objects
        const collection = col.collection || col;
        return {
          collection: {
            ...collection,
            // Ensure required fields exist with defaults
            name: collection.name || 'Untitled Collection',
            description: collection.description || '',
            stats: collection.stats || { totalProblems: 0, difficulty: { easy: 0, medium: 0, hard: 0 } }
          },
          userProgress: col.userProgress || {
            completedProblems: 0,
            totalProblems: collection.stats?.totalProblems || 0
          },
          completedCount: col.completedCount || 0,
          completionPercentage: col.completionPercentage || 0
        };
      });
      console.log('Transformed collections:', transformedCollections);
      
      // Load saved order from localStorage
      const savedOrder = localStorage.getItem('myCollectionsOrder');
      if (savedOrder && !searchQuery) {
        const orderMap = JSON.parse(savedOrder);
        // Sort collections based on saved order
        const sortedCollections = transformedCollections.sort((a: any, b: any) => {
          const orderA = orderMap[a.collection.id] ?? Number.MAX_VALUE;
          const orderB = orderMap[b.collection.id] ?? Number.MAX_VALUE;
          return orderA - orderB;
        });
        setCollections(sortedCollections);
      } else {
        setCollections(transformedCollections);
      }
    } catch (error) {
      console.error('Failed to load collections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateButtonClick = () => {
    // If not authenticated, save action before redirect
    if (!isAuthenticated) {
      savePendingAction({
        type: 'create_collection',
        data: {}
      });
      // Trigger auth flow (action will resume after redirect)
      requireAuth(() => {});
      return;
    }

    // Already authenticated, proceed immediately
    openCreateModal();
  };

  const openCreateModal = () => {
    setShowCreateModal(true);
  };

  const handleCreateCollection = async (
    name: string,
    description: string,
    baseCollectionId?: string,
    leetcodeNumbers?: number[]
  ) => {
    try {
      setShowCreateModal(false);

      // Use streaming if LeetCode numbers are provided (for real-time progress)
      if (leetcodeNumbers && leetcodeNumbers.length > 0) {
        setIsGeneratingProblems(true);
        const newCollection = await startStream({
          name,
          description,
          type: 'custom',
          baseCollectionId,
          leetcodeNumbers
        });

        setIsGeneratingProblems(false);

        if (newCollection) {
          // Navigate to the new collection page
          navigate(`/collections/${newCollection.id}`);
        }
      } else {
        // No LeetCode numbers - use regular API
        setIsCreating(true);
        const newCollection = await collectionsApi.createCollection({
          name,
          description,
          type: 'custom',
          baseCollectionId
        });
        // Navigate to the new collection page
        navigate(`/collections/${newCollection.id}`);
      }
    } catch (error) {
      console.error('Failed to create collection:', error);
      setIsCreating(false);
      setIsGeneratingProblems(false);
    }
  };

  const handleDeleteCollection = async (id: string) => {
    try {
      setIsDeleting(true);
      await collectionsApi.deleteCollection(id);
      await loadCollections();
    } catch (error) {
      console.error('Failed to delete collection:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleGenerateByAI = (generateDTO: GenerateCollectionDTO) => {
    // If not authenticated, save action before redirect
    if (!isAuthenticated) {
      savePendingAction({
        type: 'generate_collection',
        data: generateDTO
      });
      // Trigger auth flow (action will resume after redirect)
      requireAuth(() => {});
      return;
    }

    // Already authenticated, proceed immediately
    performGenerateByAI(generateDTO);
  };

  const performGenerateByAI = async (generateDTO: GenerateCollectionDTO) => {
    // ✅ Set loading state AFTER auth check passes
    setIsGeneratingAI(true);

    try {
      const collection = await collectionsApi.generateCollectionByAI(generateDTO);
      console.log('AI Generated Collection Response:', collection);

      // Check if collection was created successfully
      if (!collection || !collection.id) {
        console.error('Invalid collection response:', collection);
        alert('Failed to generate collection. The server response was invalid.');
        setIsGeneratingAI(false);
        return;
      }

      // Navigate to the new collection
      navigate(`/collections/${collection.id}`);
    } catch (error: any) {
      console.error('Failed to generate collection:', error);

      // Check if this is a usage limit error (402 Payment Required)
      const isUsageLimitError = error?.status === 402 ||
                                error?.message?.includes('usage-limit-exceeded') ||
                                error?.originalError?.status === 402;

      if (isUsageLimitError) {
        // Open paywall modal instead of showing generic error
        openPaywall('no-credits', 'collection-generation');
      } else {
        // ✅ Show generic error - auth is checked upfront, no need to check error message
        let errorMessage = 'Failed to generate collection. Please try again.';
        if (error instanceof Error && error.message) {
          errorMessage = `Failed to generate collection: ${error.message}`;
        }
        alert(errorMessage);
      }

      setIsGeneratingAI(false);
    }
  };

  const handleTogglePin = async (collectionId: string) => {
    console.log('[PIN DEBUG] handleTogglePin called for collection:', collectionId);
    const isPinned = isCollectionPinned(collectionId);
    console.log('[PIN DEBUG] isPinned:', isPinned);

    if (isPinned) {
      console.log('[PIN DEBUG] Unpinning collection...');
      const result = await unpinCollection(collectionId);
      console.log('[PIN DEBUG] Unpin result:', result);
      if (!result.success && result.message) {
        alert(result.message);
      }
    } else {
      // Check if at max pins
      console.log('[PIN DEBUG] Current pins:', pinData.pinnedCollections.length, '/ max:', maxPinnedCollections);
      if (pinData.pinnedCollections.length >= maxPinnedCollections) {
        alert(`You can only pin up to ${maxPinnedCollections} collections. Please unpin one before pinning another.`);
        return;
      }

      console.log('[PIN DEBUG] Pinning collection...');
      const result = await pinCollection(collectionId);
      console.log('[PIN DEBUG] Pin result:', result);
      if (!result.success && result.message) {
        alert(result.message);
      }
    }
  };

  const filteredCollections = collections.filter(item => {
    if (!item || !item.collection) return false;
    const name = item.collection.name || '';
    const description = item.collection.description || '';
    const query = searchQuery.toLowerCase();
    return name.toLowerCase().includes(query) ||
           description.toLowerCase().includes(query);
  });

  // Apply pin sorting to filtered collections
  const sortedCollections = sortCollections(filteredCollections);

  // Drag and drop handlers
  const handleDragStart = useCallback((e: React.DragEvent, item: CollectionListItem) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent, id: string) => {
    e.preventDefault();
    setDraggedOverId(id);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    // Only clear if we're leaving the card entirely
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!e.currentTarget.contains(relatedTarget)) {
      setDraggedOverId(null);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetItem: CollectionListItem) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem.collection.id === targetItem.collection.id) {
      setDraggedOverId(null);
      return;
    }

    // Reorder collections
    const newCollections = [...collections];
    const draggedIndex = newCollections.findIndex(item => item.collection.id === draggedItem.collection.id);
    const targetIndex = newCollections.findIndex(item => item.collection.id === targetItem.collection.id);

    if (draggedIndex !== -1 && targetIndex !== -1) {
      // Remove dragged item and insert at target position
      const [removed] = newCollections.splice(draggedIndex, 1);
      newCollections.splice(targetIndex, 0, removed);
      setCollections(newCollections);

      // Save new order to localStorage if no search is active
      if (!searchQuery) {
        const orderMap: Record<string, number> = {};
        newCollections.forEach((item, index) => {
          orderMap[item.collection.id] = index;
        });
        localStorage.setItem('myCollectionsOrder', JSON.stringify(orderMap));
      }
    }

    setDraggedItem(null);
    setDraggedOverId(null);
  }, [draggedItem, collections, searchQuery]);

  const handleDragEnd = useCallback(() => {
    setDraggedItem(null);
    setDraggedOverId(null);
  }, []);

  const handleDropAtEnd = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedItem) {
      setDraggedOverId(null);
      return;
    }

    // Move dragged item to the end
    const newCollections = collections.filter(item => item.collection.id !== draggedItem.collection.id);
    newCollections.push(draggedItem);
    setCollections(newCollections);

    // Save new order to localStorage
    if (!searchQuery) {
      const orderMap: Record<string, number> = {};
      newCollections.forEach((item, index) => {
        orderMap[item.collection.id] = index;
      });
      localStorage.setItem('myCollectionsOrder', JSON.stringify(orderMap));
    }

    setDraggedItem(null);
    setDraggedOverId(null);
  }, [draggedItem, collections, searchQuery]);

  const handleDropOnGrid = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    // This handles drops on empty grid space
    // If not dropping on a card or the end zone, move to end
    const target = e.target as HTMLElement;
    if (target.classList.contains('collections-grid') && draggedItem) {
      handleDropAtEnd(e);
    }
  }, [draggedItem, handleDropAtEnd]);

  return (
    <div className="my-collections-tab">
      <div className="collections-header">
        <h2>My Collections</h2>
        <button
          className="create-collection-btn"
          onClick={handleCreateButtonClick}
        >
          + Create
        </button>
      </div>

      <div className="search-container">
        <input
          type="text"
          placeholder="Search collections..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="collections-search"
        />
        {searchQuery && (
          <p className="search-note">Drag & drop is disabled while searching</p>
        )}
      </div>

      {authLoading || loading ? (
        <div className="loading-state">{authLoading ? 'Initializing...' : 'Loading collections...'}</div>
      ) : !isAuthenticated ? (
        <div className="empty-state">
          <p>Please sign in to view your collections.</p>
        </div>
      ) : sortedCollections.length === 0 ? (
        <div className="empty-state">
          {searchQuery ? (
            <p>No collections found matching "{searchQuery}"</p>
          ) : (
            <>
              <p>You don't have any collections yet.</p>
              <button
                className="create-first-btn"
                onClick={() => setShowCreateModal(true)}
              >
                Create Your First Collection
              </button>
            </>
          )}
        </div>
      ) : (
        <div
          className="collections-grid"
          onDragOver={handleDragOver}
          onDrop={(e) => handleDropOnGrid(e)}
        >
          {sortedCollections.map(item => {
            const collectionId = item.collection.id;
            const isPinned = isCollectionPinned(collectionId);
            // Only allow pinning for user's own collections (not system collections)
            const canPin = !!(isAuthenticated &&
                          userProfile &&
                          item.collection.ownerId === userProfile.uid);

            console.log('[PIN DEBUG] Collection:', item.collection.name, {
              collectionId,
              isPinned,
              canPin,
              isAuthenticated,
              hasUserProfile: !!userProfile,
              ownerId: item.collection.ownerId,
              userProfileUid: userProfile?.uid
            });

            return (
              <div
                key={collectionId}
                draggable={!searchQuery}
                onDragStart={(e) => handleDragStart(e, item)}
                onDragOver={handleDragOver}
                onDragEnter={(e) => handleDragEnter(e, collectionId)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, item)}
                onDragEnd={handleDragEnd}
                className={`collection-card-wrapper ${
                  draggedOverId === collectionId ? 'drag-over' : ''
                } ${draggedItem?.collection.id === collectionId ? 'dragging' : ''}`}
              >
                <CollectionCard
                  item={item}
                  onDelete={handleDeleteCollection}
                  showDragHandle={!searchQuery}
                  isPinned={isPinned}
                  onPin={() => handleTogglePin(collectionId)}
                  canPin={canPin}
                />
              </div>
            );
          })}
          {/* Drop zone for adding to end */}
          {!searchQuery && draggedItem && (
            <div 
              className={`drop-zone-end ${draggedOverId === 'end-zone' ? 'drag-over' : ''}`}
              onDragOver={handleDragOver}
              onDragEnter={(e) => handleDragEnter(e, 'end-zone')}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDropAtEnd(e)}
            >
              <div className="drop-zone-content">
                Drop here to place at end
              </div>
            </div>
          )}
        </div>
      )}

      {showCreateModal && (
        <CreateCollectionModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateCollection}
          onGenerateByAI={handleGenerateByAI}
          collections={systemCollections}
          setIsGeneratingAI={setIsGeneratingAI}
        />
      )}
      
      <LoadingModal
        isOpen={isDeleting}
        message="Deleting collection..."
      />
      
      <LoadingModal
        isOpen={isCreating}
        message="Creating collection..."
      />
      
      <LoadingModal
        isOpen={isGeneratingAI}
        message="AI is generating your personalized collection..."
      />

      <LoadingModal
        isOpen={isGeneratingProblems || streamState.isStreaming}
        message={(() => {
          const { generationState } = streamState;
          if (generationState.total === 0) {
            return "Preparing to generate problems...";
          }

          const lines: string[] = [];

          // Line 1: Currently generating
          if (generationState.generating.length > 0) {
            const nums = generationState.generating.map(n => `#${n}`).join(', ');
            lines.push(`🤖 Generating problems ${nums}...`);
          }

          // Line 2: Completion status
          const done = generationState.completed + generationState.failed;
          if (done > 0 || generationState.generating.length > 0) {
            let statusLine = `✅ ${generationState.completed} of ${generationState.total} completed`;
            if (generationState.failed > 0) {
              statusLine += ` · ❌ ${generationState.failed} failed`;
            }
            lines.push(statusLine);
          }

          return lines.length > 0 ? lines.join('\n') : "Generating LeetCode problems...";
        })()}
      />

      <AuthRequiredModal
        isOpen={showAuthRequiredModal}
        onClose={handleClose}
        onOk={handleOk}
      />

      <LoginModal
        isOpen={showLoginModal}
        onClose={handleClose}
        onSuccess={handleLoginSuccess}
      />
    </div>
  );
};